import { DestroyRef, Injectable, inject } from "@angular/core";
import { HttpResponse } from "@angular/common/http";
import { filter, from, map, Observable, OperatorFunction, pipe, switchMap, take, tap, throwError } from "rxjs";

import {
    CopySubjectResponseData,
    CreateSubjectResponseData,
    DeleteSubjectResponseData,
    FetchSubjectResponseData,
    FHIRMiddlewareApiService,
    GetReviewChangesResponseData,
    GetSubjectListResponseData,
    PostSubjectMergeChangesResponseData,
    SubjectListQuery,
    PublishSubjectResponseData,
    GetPublishedSubjectListResponseData,
    GetSubjectResponseData,
    LockResponseData,
    FilterOptionsData,
    GetExternalOptionsetResponseData,
    ROOT_DATASET_API_PATH
} from "@fhir-api";
import { AppProjectsService } from "../app-projects.service";

import { AppConfigurationService } from "../configuration/app-configuration.service";
import { AppStateService, RequestErrorActionStructure } from "./app-state.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AppEtagManagerService, HttpResponseWithEtag } from "../app-etag-manager.service";
import { IFilterList } from "@logex/framework/lg-filterset";
import { DatePipe } from "@angular/common";
import { AppStorageService } from "../app-storage.service";
import { AppSubjectLockingManagerService } from "./app-subject-locking-manager.service";
import { EXPRESSION_VALIDATOR_VERSION } from "@app/util/dependency-versions";

@Injectable({ providedIn: "root" })
export class AppSubjectApiService {
    private _appStorageService = inject(AppStorageService);
    private _appStateService = inject(AppStateService);
    private _middlewareApiService = inject(FHIRMiddlewareApiService);
    private _appConfigurationService = inject(AppConfigurationService);
    private _projectsService = inject(AppProjectsService);
    private _destroyRef = inject(DestroyRef);
    private _appEtagManagerService = inject(AppEtagManagerService);
    private _datePipe = inject(DatePipe);
    private _appSubjectLockingManagerService = inject(AppSubjectLockingManagerService);

    create$(
        variablevalues: Record<string, string>,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<CreateSubjectResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .createSubject$(organization, project, ROOT_DATASET_API_PATH, variablevalues)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    publish$(
        subjectName: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<PublishSubjectResponseData | null> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        const appVersion = this._appConfigurationService.getVersion();
        const validatorVersion = EXPRESSION_VALIDATOR_VERSION;
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .publishSubject$(
                organization,
                project,
                ROOT_DATASET_API_PATH,
                subjectName,
                appVersion,
                validatorVersion
            )
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    fetch$(
        variablevalues: Record<string, string>,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<FetchSubjectResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .fetchSubject$(organization, project, variablevalues)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    createSubjectChild$(
        datasetApiPath: string,
        parentSubjectName: string,
        variablevalues: Record<string, string>,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<CreateSubjectResponseData> {
        return this._handleLockedOperationRequest$((etag: string) => {
            const organization = this._appStateService.selectedOrganization();
            const project = this._appStateService.selectedRegistryName();
            if (!organization || !project) {
                return throwError(() => new Error("Organization or project not found"));
            }
            return this._middlewareApiService.createSubjectChild$(
                organization,
                project,
                datasetApiPath,
                parentSubjectName,
                variablevalues,
                etag
            );
        }, requestErrorAction);
    }

    reviewChanges$(
        subjectName: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<GetReviewChangesResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .getSubjectReviewChanges$(organization, project, ROOT_DATASET_API_PATH, subjectName)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    mergeChanges$(
        subjectName: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<PostSubjectMergeChangesResponseData> {
        return this._handleLockedOperationRequest$((etag: string) => {
            const organization = this._appStateService.selectedOrganization();
            const project = this._appStateService.selectedRegistryName();
            if (!organization || !project) {
                return throwError(() => new Error("Organization or project not found"));
            }
            return this._middlewareApiService.postSubjectMergeChanges$(
                organization,
                project,
                ROOT_DATASET_API_PATH,
                subjectName,
                etag
            );
        }, requestErrorAction);
    }

    copy$(
        datasetApiPath: string,
        subjectApiPath: string,
        initialValues: Record<string, string>,
        excludedFields: string[] = [],
        parentSubjectApiPath?: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<CopySubjectResponseData> {
        return this._handleLockedOperationRequest$((etag: string) => {
            const organization = this._appStateService.selectedOrganization();
            const project = this._appStateService.selectedRegistryName();
            if (!organization || !project) {
                return throwError(() => new Error("Organization or project not found"));
            }
            return this._middlewareApiService.copySubject$(
                organization,
                project,
                datasetApiPath,
                subjectApiPath,
                parentSubjectApiPath,
                etag,
                initialValues,
                excludedFields
            );
        }, requestErrorAction);
    }

    tryLock$(
        subjectName: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<LockResponseData> {
        return this._handleLockedOperationRequest$<LockResponseData>(
            (etag: string) => {
                const lockedSubjectName =
                    this._appStorageService.getFromSessionStorage("lockedSubjectName");
                const unlockPromise = new Promise<void>(resolve => {
                    if (lockedSubjectName && lockedSubjectName !== subjectName) {
                        this._appSubjectLockingManagerService.unlockSubject().then(() => {
                            resolve();
                        });
                    } else {
                        resolve();
                    }
                });
                return from(unlockPromise).pipe(
                    take(1),
                    tap(() => {
                        this._appStorageService.setToSessionStorage("lockedSubjectName", subjectName);
                    }),
                    switchMap(() => {
                        return this._appSubjectLockingManagerService.lock$(subjectName, etag);
                    })
                );
            },
            {
                ...requestErrorAction,
                default: {
                    action: () => {
                        this._appStorageService.removeFromSessionStorage("lockedSubjectName");
                    }
                }
            }
        );
    }

    deleteSubject$(
        datasetApiPath: string,
        subjectApiPath: string,
        deletePublished: boolean = false,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<DeleteSubjectResponseData> {
        return this._handleLockedOperationRequest$((etag: string) => {
            const organization = this._appStateService.selectedOrganization();
            const project = this._appStateService.selectedRegistryName();
            if (!organization || !project) {
                return throwError(() => new Error("Organization or project not found"));
            }
            return this._middlewareApiService.deleteSubject$(
                organization,
                project,
                datasetApiPath,
                subjectApiPath,
                deletePublished,
                { etag }
            );
        }, requestErrorAction);
    }

    list$(
        pageNumber: number,
        pageSize: number,
        listType: "draft" | "published",
        params?: {
            sortType?: "ascending" | "descending";
            sortBy?: string;
            filterBy?: IFilterList;
        },
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<GetSubjectListResponseData | GetPublishedSubjectListResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        const columns = this._projectsService.settings().listPage.columns.reduce((acc, column) => {
            acc.push(column.name);
            if (column.hasOther) {
                acc.push(column.name + "other");
            }
            return acc;
        }, [] as string[]);
        columns.push("subjectLastEdited");
        const listQuery: SubjectListQuery = {
            pageNumber: pageNumber - 1,
            pageSize,
            columns,
            sortBy: params?.sortBy || this._projectsService.settings().listPage.defaultSortBy,
            sortType: params?.sortType || this._projectsService.settings().listPage.defaultSortOrder
        };
        if (params?.filterBy) {
            const searchCriteria: Record<string, string> = {};
            for (const filter in params.filterBy) {
                if (params.filterBy[filter]) {
                    if (typeof params.filterBy[filter] === "string") {
                        searchCriteria[filter] = params.filterBy[filter] as string;
                    } else {
                        if (params.filterBy[filter]["enabled"] !== undefined) {
                            if (params.filterBy[filter]["enabled"]) {
                                const fromValue = params.filterBy[filter]["from"] ?? "";
                                const from =
                                    fromValue instanceof Date
                                        ? (this._datePipe.transform(fromValue, "yyyy-MM-dd") ?? "")
                                        : fromValue;
                                const toValue = params.filterBy[filter]["to"] ?? "";
                                const to =
                                    toValue instanceof Date
                                        ? (this._datePipe.transform(toValue, "yyyy-MM-dd") ?? "")
                                        : toValue;
                                searchCriteria[filter] = `${from}~${to}`;
                            }
                        } else {
                            if (params.filterBy[filter]["$empty"] !== true) {
                                searchCriteria[filter] = Object.keys(params.filterBy[filter]).join(
                                    "|"
                                );
                            }
                        }
                    }
                }
            }
            listQuery.searchCriteria = searchCriteria;
        }
        if (listType === "published") {
            return this._middlewareApiService
                .getPublishedList$(organization, project, {
                    ...listQuery,
                    sortBy: params?.sortBy,
                    sortType: params?.sortType
                })
                .pipe(this._handleOperationRequest$(requestErrorAction));
        }
        return this._middlewareApiService
            .getSubjectList$(organization, project, listQuery)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    getSubject$(
        subjectApiPath: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<GetSubjectResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .getSubject$(organization, project, ROOT_DATASET_API_PATH, subjectApiPath)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    filterOptions$(
        variableName: string,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<FilterOptionsData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .getFilterOptions$(organization, project, variableName)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    externalOptionset$(
        name: string,
        searchCriteria?: Record<string, string>,
        requestErrorAction?: RequestErrorActionStructure
    ): Observable<GetExternalOptionsetResponseData> {
        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) {
            return throwError(() => new Error("Organization or project not found"));
        }
        return this._middlewareApiService
            .getExternalOptionset$(organization, project, name, searchCriteria)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    publishedPatient$(
        projectName: string,
        datasetName: string,
        naturalKeys: Record<string, string>,
        fields: string[],
        requestErrorAction?: RequestErrorActionStructure
    ) {
        const organization = this._appStateService.selectedOrganization();
        if (!organization) {
            return throwError(() => new Error("Organization not found"));
        }
        return this._middlewareApiService
            .getPublishedPatient$(organization, projectName, datasetName, naturalKeys, fields)
            .pipe(this._handleOperationRequest$(requestErrorAction));
    }

    private _handleOperationRequest$<T>(
        requestErrorAction?: RequestErrorActionStructure
    ): OperatorFunction<HttpResponse<T>, T> {
        return pipe(
            tap((response: HttpResponse<T>) => {
                if (!response.body) {
                    throw new Error("Response has been received without body.");
                }
            }),
            this._appStateService.catchRequestError$(requestErrorAction),
            filter(response => response != null),
            map(response => response.body!),
            takeUntilDestroyed(this._destroyRef)
        );
    }

    private _handleLockedOperationRequest$<T>(
        requestInvoker: (etag: string) => Observable<HttpResponseWithEtag<T>>,
        requestErrorAction?: RequestErrorActionStructure
    ) {
        return this._appEtagManagerService
            .invokeRequestWithEtagRefresh$<T>((etag: string) => requestInvoker(etag))
            .pipe(this._handleOperationRequest$<T>(requestErrorAction));
    }
}
